import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import styled from 'styled-components';
import { 
  FiTruck, 
  FiMapPin, 
  FiClock, 
  FiPackage, 
  FiThermometer, 
  FiDroplet, 
  FiBarChart, 
  FiEye, 
  FiNavigation,
  FiShield,
  FiActivity,
  FiCheck,
  FiAlertTriangle
} from 'react-icons/fi';

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const TrackingMapWrapper = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  overflow: hidden;
`;

const MapHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const MapTitle = styled.h2`
  color: #fff;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .icon {
    color: #4facfe;
  }
`;

const MapControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.2rem;
  background: ${props => props.active ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.active ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 10px;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' : 'rgba(79, 172, 254, 0.1)'};
    border-color: #4facfe;
  }
`;

const MapSection = styled.div`
  height: 600px;
  border-radius: 15px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  
  .leaflet-container {
    height: 100%;
    background: linear-gradient(135deg, #1a1a3e 0%, #2a2a5e 100%);
    border-radius: 15px;
  }
  
  .leaflet-tile {
    filter: brightness(0.4) contrast(1.5) saturate(0.5) hue-rotate(220deg) invert(1);
  }
  
  .leaflet-popup-content-wrapper {
    background: rgba(15, 15, 35, 0.95);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(79, 172, 254, 0.3);
    border-radius: 12px;
    color: #fff;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
  
  .leaflet-popup-tip {
    background: rgba(15, 15, 35, 0.95);
    border: 1px solid rgba(79, 172, 254, 0.3);
  }
`;

const ShipmentSidebar = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 350px;
  height: 100%;
  background: rgba(15, 15, 35, 0.95);
  backdrop-filter: blur(10px);
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1.5rem;
  overflow-y: auto;
  z-index: 1000;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  h3 {
    color: #fff;
    font-size: 1.2rem;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a0a0a0;
  cursor: pointer;
  font-size: 1.2rem;
  padding: 0.5rem;
  
  &:hover {
    color: #fff;
  }
`;

const ShipmentCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(79, 172, 254, 0.1);
    border-color: #4facfe;
  }
  
  .shipment-id {
    color: #4facfe;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  .shipment-route {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 0.8rem;
  }
  
  .shipment-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    
    .status-icon {
      font-size: 0.9rem;
    }
    
    .status-text {
      color: #fff;
      font-size: 0.9rem;
    }
  }
  
  .shipment-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    font-size: 0.8rem;
    
    .detail-item {
      .label {
        color: #a0a0a0;
      }
      .value {
        color: #fff;
        font-weight: 500;
      }
    }
  }
`;

const StatusIndicator = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: rgba(15, 15, 35, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 1rem;
  z-index: 1000;
  
  .status-title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.8rem;
    font-size: 0.9rem;
  }
  
  .status-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .status-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    
    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      
      &.in-transit { background: #4facfe; }
      &.delivered { background: #00ff88; }
      &.delayed { background: #ff6b6b; }
      &.scheduled { background: #ffd93d; }
    }
    
    .status-count {
      color: #fff;
      font-weight: 500;
    }
    
    .status-label {
      color: #a0a0a0;
    }
  }
`;

const MapLegend = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  background: rgba(15, 15, 35, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 1rem;
  z-index: 1000;
  min-width: 200px;
  
  .legend-title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.8rem;
    font-size: 0.9rem;
  }
  
  .legend-section {
    margin-bottom: 0.8rem;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .section-title {
      color: #a0a0a0;
      font-size: 0.75rem;
      text-transform: uppercase;
      margin-bottom: 0.4rem;
      letter-spacing: 0.5px;
    }
    
    .legend-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.3rem;
      font-size: 0.8rem;
      
      .legend-icon {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 10px;
        border: 2px solid #fff;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      }
      
      .legend-label {
        color: #fff;
      }
    }
  }
`;

// Custom marker icons
const createCustomIcon = (type, color = '#4facfe') => {
  return L.divIcon({
    html: `
      <div style="
        background: ${color};
        border: 3px solid #fff;
        border-radius: 50%;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 12px;
        box-shadow: 0 4px 15px rgba(0,0,0,0.3);
      ">
        ${type === 'warehouse' ? '🏭' : type === 'port' ? '🚢' : type === 'truck' ? '🚚' : '📦'}
      </div>
    `,
    className: 'custom-marker',
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });
};

function TrackingMap() {
  const [activeView, setActiveView] = useState('live');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [shipments, setShipments] = useState([]);

  // Mock shipment data with real coordinates
  useEffect(() => {
    const mockShipments = [
      {
        id: 'SUG-SHIP-001',
        commodity: 'Raw Sugar',
        quantity: '25,000 MT',
        origin: { name: 'Santos, Brazil', lat: -24.0092, lng: -46.3628 },
        destination: { name: 'Dubai, UAE', lat: 25.2048, lng: 55.2708 },
        currentLocation: { lat: -10.5, lng: -25.0 },
        status: 'In Transit',
        statusColor: '#4facfe',
        vessel: 'MV Sugar Express',
        eta: '2024-01-25',
        temperature: '24°C',
        humidity: '45%',
        route: [
          [-24.0092, -46.3628], // Santos
          [-20.0, -35.0],       // Atlantic Ocean waypoint 1
          [-10.5, -25.0],       // Current position
          [0.0, -15.0],         // Atlantic Ocean waypoint 2
          [15.0, 20.0],         // Red Sea
          [25.2048, 55.2708]    // Dubai
        ],
        events: [
          { time: '2024-01-15 08:00', event: 'Departed Santos Port', location: 'Santos, Brazil' },
          { time: '2024-01-18 14:30', event: 'Passed Equator', location: 'Atlantic Ocean' },
          { time: '2024-01-20 09:15', event: 'Current Position', location: 'Atlantic Ocean' }
        ]
      },
      {
        id: 'SUG-SHIP-002',
        commodity: 'Refined Sugar',
        quantity: '15,000 MT',
        origin: { name: 'Bangkok, Thailand', lat: 13.7563, lng: 100.5018 },
        destination: { name: 'Singapore', lat: 1.3521, lng: 103.8198 },
        currentLocation: { lat: 7.5, lng: 102.0 },
        status: 'In Transit',
        statusColor: '#4facfe',
        vessel: 'MV Thai Trader',
        eta: '2024-01-22',
        temperature: '26°C',
        humidity: '60%',
        route: [
          [13.7563, 100.5018], // Bangkok
          [7.5, 102.0],         // Current position
          [1.3521, 103.8198]    // Singapore
        ],
        events: [
          { time: '2024-01-19 10:00', event: 'Departed Bangkok Port', location: 'Bangkok, Thailand' },
          { time: '2024-01-20 16:45', event: 'Current Position', location: 'Gulf of Thailand' }
        ]
      },
      {
        id: 'SUG-SHIP-003',
        commodity: 'Raw Sugar',
        quantity: '30,000 MT',
        origin: { name: 'Mumbai, India', lat: 19.0760, lng: 72.8777 },
        destination: { name: 'Rotterdam, Netherlands', lat: 51.9244, lng: 4.4777 },
        currentLocation: { lat: 19.0760, lng: 72.8777 },
        status: 'Loading',
        statusColor: '#ffd93d',
        vessel: 'MV Indian Ocean',
        eta: '2024-02-10',
        temperature: '28°C',
        humidity: '55%',
        route: [
          [19.0760, 72.8777],   // Mumbai
          [51.9244, 4.4777]     // Rotterdam
        ],
        events: [
          { time: '2024-01-20 06:00', event: 'Arrived at Mumbai Port', location: 'Mumbai, India' },
          { time: '2024-01-20 14:00', event: 'Started Loading', location: 'Mumbai, India' }
        ]
      }
    ];

    setShipments(mockShipments);
  }, []);

  // Mock warehouse data
  const warehouses = [
    {
      id: 'WH-DUBAI-001',
      name: 'Dubai DMCC Warehouse',
      location: { lat: 25.2048, lng: 55.2708 },
      capacity: '100,000 MT',
      currentStock: '65,000 MT',
      utilization: 65,
      commodities: ['Raw Sugar', 'Refined Sugar'],
      temperature: '22°C',
      humidity: '40%',
      certifications: ['ISO 9001', 'HACCP'],
      status: 'Active'
    },
    {
      id: 'WH-SANTOS-001',
      name: 'Santos Port Storage',
      location: { lat: -24.0092, lng: -46.3628 },
      capacity: '150,000 MT',
      currentStock: '95,000 MT',
      utilization: 63,
      commodities: ['Raw Sugar'],
      temperature: '24°C',
      humidity: '45%',
      certifications: ['ISO 9001', 'Organic'],
      status: 'Active'
    },
    {
      id: 'WH-BANGKOK-001',
      name: 'Bangkok Commodity Center',
      location: { lat: 13.7563, lng: 100.5018 },
      capacity: '80,000 MT',
      currentStock: '52,000 MT',
      utilization: 65,
      commodities: ['Refined Sugar', 'Raw Sugar'],
      temperature: '26°C',
      humidity: '55%',
      certifications: ['ISO 9001', 'HACCP'],
      status: 'Active'
    },
    {
      id: 'WH-MUMBAI-001',
      name: 'Mumbai Storage Terminal',
      location: { lat: 19.0760, lng: 72.8777 },
      capacity: '120,000 MT',
      currentStock: '78,000 MT',
      utilization: 65,
      commodities: ['Raw Sugar', 'Refined Sugar'],
      temperature: '28°C',
      humidity: '50%',
      certifications: ['ISO 9001', 'Fair Trade'],
      status: 'Active'
    },
    {
      id: 'WH-SINGAPORE-001',
      name: 'Singapore Trade Hub',
      location: { lat: 1.3521, lng: 103.8198 },
      capacity: '90,000 MT',
      currentStock: '45,000 MT',
      utilization: 50,
      commodities: ['Refined Sugar'],
      temperature: '25°C',
      humidity: '60%',
      certifications: ['ISO 9001', 'HACCP', 'Halal'],
      status: 'Active'
    },
    {
      id: 'WH-ROTTERDAM-001',
      name: 'Rotterdam Distribution Center',
      location: { lat: 51.9244, lng: 4.4777 },
      capacity: '200,000 MT',
      currentStock: '125,000 MT',
      utilization: 63,
      commodities: ['Raw Sugar', 'Refined Sugar'],
      temperature: '18°C',
      humidity: '35%',
      certifications: ['ISO 9001', 'Organic', 'Fair Trade'],
      status: 'Active'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'In Transit': return <FiTruck className="status-icon" />;
      case 'Loading': return <FiPackage className="status-icon" />;
      case 'Delivered': return <FiCheck className="status-icon" />;
      case 'Delayed': return <FiAlertTriangle className="status-icon" />;
      default: return <FiClock className="status-icon" />;
    }
  };

  const handleShipmentClick = (shipment) => {
    setSelectedShipment(shipment);
    setShowSidebar(true);
  };

  const getShipmentCounts = () => {
    const counts = shipments.reduce((acc, shipment) => {
      acc[shipment.status] = (acc[shipment.status] || 0) + 1;
      return acc;
    }, {});
    
    return {
      'In Transit': counts['In Transit'] || 0,
      'Loading': counts['Loading'] || 0,
      'Delivered': counts['Delivered'] || 0,
      'Delayed': counts['Delayed'] || 0
    };
  };

  const statusCounts = getShipmentCounts();

  return (
    <TrackingMapWrapper>
      <MapHeader>
        <MapTitle>
          <FiMapPin className="icon" />
          Global Commodity Tracking
        </MapTitle>
        <MapControls>
          <ControlButton 
            active={activeView === 'live'} 
            onClick={() => setActiveView('live')}
          >
            <FiActivity />
            Live Tracking
          </ControlButton>
          <ControlButton 
            active={activeView === 'routes'} 
            onClick={() => setActiveView('routes')}
          >
            <FiNavigation />
            Shipping Routes
          </ControlButton>
          <ControlButton 
            active={activeView === 'warehouses'} 
            onClick={() => setActiveView('warehouses')}
          >
            <FiPackage />
            Warehouses
          </ControlButton>
          <ControlButton onClick={() => setShowSidebar(!showSidebar)}>
            <FiEye />
            Shipments ({shipments.length})
          </ControlButton>
        </MapControls>
      </MapHeader>

      <MapSection>
        <MapContainer
          center={[10, 20]}
          zoom={3}
          style={{ height: '100%', width: '100%', minHeight: '600px' }}
          whenCreated={(map) => {
            setTimeout(() => {
              map.invalidateSize();
            }, 100);
          }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          {/* Shipment markers and routes */}
          {shipments.map(shipment => (
            <React.Fragment key={shipment.id}>
              {/* Route line */}
              {activeView === 'routes' && shipment.route && (
                <Polyline
                  positions={shipment.route}
                  color={shipment.statusColor}
                  weight={3}
                  opacity={0.8}
                  dashArray="10, 10"
                />
              )}
              
              {/* Origin marker */}
              <Marker
                position={[shipment.origin.lat, shipment.origin.lng]}
                icon={createCustomIcon('warehouse', '#00ff88')}
              >
                <Popup>
                  <div style={{ color: '#fff', minWidth: '200px' }}>
                    <strong>Origin: {shipment.origin.name}</strong><br/>
                    Shipment: {shipment.id}<br/>
                    Commodity: {shipment.commodity}<br/>
                    Quantity: {shipment.quantity}
                  </div>
                </Popup>
              </Marker>
              
              {/* Destination marker */}
              <Marker
                position={[shipment.destination.lat, shipment.destination.lng]}
                icon={createCustomIcon('port', '#ff6b6b')}
              >
                <Popup>
                  <div style={{ color: '#fff', minWidth: '200px' }}>
                    <strong>Destination: {shipment.destination.name}</strong><br/>
                    Shipment: {shipment.id}<br/>
                    ETA: {shipment.eta}<br/>
                    Status: {shipment.status}
                  </div>
                </Popup>
              </Marker>
              
              {/* Current position marker */}
              {activeView === 'live' && (
                <Marker
                  position={[shipment.currentLocation.lat, shipment.currentLocation.lng]}
                  icon={createCustomIcon('truck', shipment.statusColor)}
                  eventHandlers={{
                    click: () => handleShipmentClick(shipment),
                  }}
                >
                  <Tooltip permanent direction="top">
                    <div style={{ 
                      background: 'rgba(15, 15, 35, 0.9)', 
                      color: '#fff', 
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontWeight: '500'
                    }}>
                      {shipment.id}
                    </div>
                  </Tooltip>
                  <Popup>
                    <div style={{ color: '#fff', minWidth: '250px' }}>
                      <strong>{shipment.id}</strong><br/>
                      Vessel: {shipment.vessel}<br/>
                      Status: {shipment.status}<br/>
                      Commodity: {shipment.commodity}<br/>
                      Quantity: {shipment.quantity}<br/>
                      Temperature: {shipment.temperature}<br/>
                      Humidity: {shipment.humidity}<br/>
                      ETA: {shipment.eta}
                    </div>
                  </Popup>
                </Marker>
              )}
            </React.Fragment>
          ))}
          
          {/* Warehouse markers */}
          {activeView === 'warehouses' && warehouses.map(warehouse => (
            <Marker
              key={warehouse.id}
              position={[warehouse.location.lat, warehouse.location.lng]}
              icon={createCustomIcon('warehouse', warehouse.utilization > 70 ? '#ff6b6b' : warehouse.utilization > 50 ? '#ffd93d' : '#00ff88')}
            >
              <Popup>
                <div style={{ color: '#fff', minWidth: '300px' }}>
                  <strong>{warehouse.name}</strong><br/>
                  <strong>ID:</strong> {warehouse.id}<br/>
                  <strong>Capacity:</strong> {warehouse.capacity}<br/>
                  <strong>Current Stock:</strong> {warehouse.currentStock}<br/>
                  <strong>Utilization:</strong> {warehouse.utilization}%<br/>
                  <strong>Temperature:</strong> {warehouse.temperature}<br/>
                  <strong>Humidity:</strong> {warehouse.humidity}<br/>
                  <strong>Commodities:</strong> {warehouse.commodities.join(', ')}<br/>
                  <strong>Certifications:</strong> {warehouse.certifications.join(', ')}<br/>
                  <strong>Status:</strong> <span style={{color: '#00ff88'}}>{warehouse.status}</span>
                </div>
              </Popup>
              <Tooltip direction="top" permanent>
                <div style={{ 
                  background: 'rgba(15, 15, 35, 0.9)', 
                  color: '#fff', 
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontWeight: '500'
                }}>
                  {warehouse.name}<br/>
                  {warehouse.utilization}% Full
                </div>
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>

        {/* Status indicator overlay */}
        <StatusIndicator>
          <div className="status-title">
            {activeView === 'live' && 'Live Shipment Status'}
            {activeView === 'routes' && 'Shipping Routes Overview'}
            {activeView === 'warehouses' && 'Warehouse Utilization'}
          </div>
          <div className="status-list">
            {activeView === 'live' && (
              <>
                <div className="status-item">
                  <div className="status-dot in-transit"></div>
                  <span className="status-count">{statusCounts['In Transit']}</span>
                  <span className="status-label">In Transit</span>
                </div>
                <div className="status-item">
                  <div className="status-dot scheduled"></div>
                  <span className="status-count">{statusCounts['Loading']}</span>
                  <span className="status-label">Loading</span>
                </div>
                <div className="status-item">
                  <div className="status-dot delivered"></div>
                  <span className="status-count">{statusCounts['Delivered']}</span>
                  <span className="status-label">Delivered</span>
                </div>
                <div className="status-item">
                  <div className="status-dot delayed"></div>
                  <span className="status-count">{statusCounts['Delayed']}</span>
                  <span className="status-label">Delayed</span>
                </div>
              </>
            )}
            {activeView === 'routes' && (
              <>
                <div className="status-item">
                  <div className="status-dot in-transit"></div>
                  <span className="status-count">{shipments.length}</span>
                  <span className="status-label">Active Routes</span>
                </div>
                <div className="status-item">
                  <div className="status-dot delivered"></div>
                  <span className="status-count">{warehouses.length}</span>
                  <span className="status-label">Ports & Warehouses</span>
                </div>
              </>
            )}
            {activeView === 'warehouses' && (
              <>
                <div className="status-item">
                  <div className="status-dot delivered"></div>
                  <span className="status-count">{warehouses.filter(w => w.utilization <= 50).length}</span>
                  <span className="status-label">Low Utilization</span>
                </div>
                <div className="status-item">
                  <div className="status-dot scheduled"></div>
                  <span className="status-count">{warehouses.filter(w => w.utilization > 50 && w.utilization <= 70).length}</span>
                  <span className="status-label">Medium Utilization</span>
                </div>
                <div className="status-item">
                  <div className="status-dot delayed"></div>
                  <span className="status-count">{warehouses.filter(w => w.utilization > 70).length}</span>
                  <span className="status-label">High Utilization</span>
                </div>
                <div className="status-item">
                  <div className="status-dot in-transit"></div>
                  <span className="status-count">{warehouses.reduce((sum, w) => sum + parseInt(w.currentStock.replace(/,/g, '').replace(' MT', '')), 0).toLocaleString()}</span>
                  <span className="status-label">Total MT Stored</span>
                </div>
              </>
            )}
          </div>
        </StatusIndicator>

        {/* Shipment sidebar */}
        <ShipmentSidebar isOpen={showSidebar}>
          <SidebarHeader>
            <h3>Active Shipments</h3>
            <CloseButton onClick={() => setShowSidebar(false)}>×</CloseButton>
          </SidebarHeader>
          
          {shipments.map(shipment => (
            <ShipmentCard 
              key={shipment.id}
              onClick={() => handleShipmentClick(shipment)}
            >
              <div className="shipment-id">{shipment.id}</div>
              <div className="shipment-route">
                {shipment.origin.name} → {shipment.destination.name}
              </div>
              <div className="shipment-status">
                {getStatusIcon(shipment.status)}
                <span className="status-text">{shipment.status}</span>
              </div>
              <div className="shipment-details">
                <div className="detail-item">
                  <div className="label">Commodity:</div>
                  <div className="value">{shipment.commodity}</div>
                </div>
                <div className="detail-item">
                  <div className="label">Quantity:</div>
                  <div className="value">{shipment.quantity}</div>
                </div>
                <div className="detail-item">
                  <div className="label">Vessel:</div>
                  <div className="value">{shipment.vessel}</div>
                </div>
                <div className="detail-item">
                  <div className="label">ETA:</div>
                  <div className="value">{shipment.eta}</div>
                </div>
              </div>
            </ShipmentCard>
          ))}
        </ShipmentSidebar>

        {/* Map Legend */}
        <MapLegend>
          <div className="legend-title">Map Legend</div>
          
          {activeView === 'live' && (
            <>
              <div className="legend-section">
                <div className="section-title">Shipment Status</div>
                <div className="legend-item">
                  <div className="legend-icon" style={{ background: '#4facfe' }}>🚢</div>
                  <div className="legend-label">In Transit</div>
                </div>
                <div className="legend-item">
                  <div className="legend-icon" style={{ background: '#ffd93d' }}>📦</div>
                  <div className="legend-label">Loading</div>
                </div>
                <div className="legend-item">
                  <div className="legend-icon" style={{ background: '#00ff88' }}>✓</div>
                  <div className="legend-label">Delivered</div>
                </div>
                <div className="legend-item">
                  <div className="legend-icon" style={{ background: '#ff6b6b' }}>⚠</div>
                  <div className="legend-label">Delayed</div>
                </div>
              </div>
            </>
          )}
          
          {activeView === 'routes' && (
            <>
              <div className="legend-section">
                <div className="section-title">Shipping Routes</div>
                <div className="legend-item">
                  <div className="legend-icon" style={{ background: '#4facfe' }}>🚢</div>
                  <div className="legend-label">Active Shipments</div>
                </div>
                <div className="legend-item">
                  <div className="legend-icon" style={{ background: '#00ff88' }}>🏭</div>
                  <div className="legend-label">Warehouses</div>
                </div>
              </div>
            </>
          )}
          
          {activeView === 'warehouses' && (
            <>
              <div className="legend-section">
                <div className="section-title">Warehouse Utilization</div>
                <div className="legend-item">
                  <div className="legend-icon" style={{ background: '#00ff88' }}>🏭</div>
                  <div className="legend-label">Low (≤50%)</div>
                </div>
                <div className="legend-item">
                  <div className="legend-icon" style={{ background: '#ffd93d' }}>🏭</div>
                  <div className="legend-label">Medium (51-70%)</div>
                </div>
                <div className="legend-item">
                  <div className="legend-icon" style={{ background: '#ff6b6b' }}>🏭</div>
                  <div className="legend-label">High (&gt;70%)</div>
                </div>
              </div>
            </>
          )}
        </MapLegend>
      </MapSection>
    </TrackingMapWrapper>
  );
}

export default TrackingMap; 